"use client";

import { APIError } from "libs/api/client";
import { useTransaction } from "libs/hooks/useTransaction";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { verifyTotp } from "../client_apis";
import { TOTPFormValues } from "../types";

type Params = {
  onLoginSucceeded: () => void;
};

const defaultValues: TOTPFormValues = {
  totp_code: "",
};

export const useTotpForm = ({ onLoginSucceeded }: Params) => {
  const [error, setError] = useState<null | "invalid" | "network">(null);

  const errorMessage = useMemo(() => {
    if (error == null) {
      return undefined;
    } else if (error === "invalid") {
      return "認証コードが正しくありません";
    } else if (error === "network") {
      return "通信エラーが発生しました";
    }
  }, [error]);

  const form = useForm<TOTPFormValues>({
    shouldUnregister: false,
    defaultValues,
  });

  const { watch } = form;

  // フォームがバリデーションを通過するかチェック
  const formFullFilled = watch("totp_code")?.length === 6;

  const [submitStatus, handleSubmitImpl] = useTransaction(async (props: typeof defaultValues) => {
    setError(null);
    await verifyTotp(props.totp_code);
  });

  // ログイン成功時の処理
  useEffect(() => {
    if (submitStatus.complete && !submitStatus.error) {
      onLoginSucceeded();
    }
  }, [submitStatus.complete, submitStatus.error, onLoginSucceeded]);

  // エラー処理
  useEffect(() => {
    if (submitStatus.error == null) {
      return;
    }

    const e = submitStatus.error;
    if (e instanceof APIError) {
      if (e.status === 401) {
        setError("invalid");
      } else {
        setError("network");
      }
    }
  }, [submitStatus.error]);

  return {
    form,
    formFullFilled,
    errorMessage,
    submitStatus,
    handleSubmit: form.handleSubmit(handleSubmitImpl),
  };
};
